import useTranslation from "@/hooks/use-translation";
import { Content } from "@/models/document";
import { Card, CardBody, Chip, Typography, Divider } from "@/components/ui";
import { FaClock, FaGlobe, FaShare, FaHeart, FaComment, FaRetweet } from "react-icons/fa";
import React from "react";
import { safeString } from "@/utils/safe-value";

interface CardViewProps {
  items: Content[];
  displayMode?: string;
}

const CardView: React.FC<CardViewProps> = ({
  items,
  displayMode = "normal",
}) => {
  const { t } = useTranslation();

  const formatDate = (date: string) => {
    return new Date(date).toLocaleDateString("fr-FR", {
      day: "numeric",
      month: "long",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  const getAuthorInfo = (item: any) => {
    // If item has normalized, use normalized; otherwise use item directly
    const rawData = item.normalized ? item.normalized : item;
    return {
      username: safeString(rawData.author?.username || rawData.author?.title) || "Unknown",
      title: safeString(rawData.author?.title || rawData.author?.name),
      avatar: safeString(rawData.author?.media?.[0]?.url || rawData.author?.avatar),
      verified: rawData.author?.verified,
      description: safeString(rawData.author?.description || rawData.author?.bio),
    };
  };

  const getContentInfo = (item: any) => {
    // If item has normalized, use normalized; otherwise use item directly
    const rawData = item.normalized ? item.normalized : item;
    return {
      title: safeString(rawData.title),
      text: safeString(rawData.text || rawData.description),
      date: safeString(rawData.date || rawData.published_time),
      language: safeString(rawData.language || rawData.lang || item.extra?.lang),
      url: safeString(rawData.url),
      stats: rawData.stats,
      media: rawData.media && rawData.media.length > 0
        ? rawData.media
        : rawData.image || rawData.thumbnail
          ? [
              {
                type: "image",
                url: safeString(rawData.image || rawData.thumbnail),
                thumbnail: safeString(rawData.thumbnail),
              },
            ]
          : [],
    };
  };

  const getStatsIcon = (key: string) => {
    switch (key) {
      case "likes":
        return <FaHeart className="size-4" />;
      case "comments":
        return <FaComment className="size-4" />;
      case "shares":
        return <FaRetweet className="size-4" />;
      default:
        return null;
    }
  };

  return (
    <div className="grid grid-cols-1 gap-6 lg:grid-cols-2">
      {items.map((item, index) => {
        const authorInfo = getAuthorInfo(item);
        const contentInfo = getContentInfo(item);

        return (
          <Card
            key={item.id || index}
            className="h-full cursor-pointer transition-all duration-300 hover:shadow-arc-lg"
            onClick={() =>
              contentInfo.url && window.open(contentInfo.url, "_blank")
            }
          >
            <CardBody className="flex h-full flex-col p-0">
              {/* Header avec auteur */}
              <div className="p-6 pb-4">
                <div className="flex items-start space-x-4">
                  <div className="size-14 shrink-0 rounded-full overflow-hidden bg-surface-secondary">
                    {authorInfo.avatar ? (
                      <img
                        src={authorInfo.avatar}
                        alt={authorInfo.username}
                        className="size-full object-cover"
                      />
                    ) : (
                      <div className="size-full flex items-center justify-center text-content-tertiary text-xl font-semibold">
                        {authorInfo.username.charAt(0).toUpperCase()}
                      </div>
                    )}
                  </div>
                  <div className="min-w-0 flex-1">
                    <div className="mb-1 flex items-center space-x-2">
                      <Typography
                        variant="h6"
                        className="truncate font-semibold"
                      >
                        {authorInfo.username}
                      </Typography>
                      {authorInfo.title && (
                        <Typography
                          variant="text-sm"
                          className="truncate"
                          color="secondary"
                        >
                          {authorInfo.title}
                        </Typography>
                      )}
                      {authorInfo.verified && (
                        <Chip size="sm" variant="soft" color="info">✓</Chip>
                      )}
                    </div>
                    {authorInfo.description && (
                      <Typography
                        variant="text-xs"
                        className="line-clamp-2"
                        color="tertiary"
                      >
                        {authorInfo.description}
                      </Typography>
                    )}
                  </div>
                </div>
              </div>

              {/* Média */}
              {contentInfo.media && contentInfo.media.length > 0 && (
                <div className="px-6 pb-4">
                  <div className="relative h-48 overflow-hidden rounded-arc-lg bg-surface-secondary">
                    <img
                      src={
                        contentInfo.media[0].url ||
                        contentInfo.media[0].thumbnail
                      }
                      alt={contentInfo.title}
                      className="size-full object-cover"
                    />
                  </div>
                </div>
              )}

              {/* Contenu */}
              <div className="flex-1 px-6 pb-4">
                {contentInfo.title && (
                  <Typography
                    variant="h6"
                    className="mb-3 line-clamp-2 font-semibold"
                  >
                    {contentInfo.title}
                  </Typography>
                )}
                <Typography
                  variant="text-sm"
                  className="line-clamp-4 leading-relaxed"
                  color="secondary"
                >
                  {contentInfo.text}
                </Typography>
              </div>

              <Divider />

              {/* Footer avec stats et métadonnées */}
              <div className="p-6 pt-4">
                {/* Statistiques */}
                {contentInfo.stats && (
                  <div className="mb-4 flex flex-wrap gap-4">
                    {Object.entries(contentInfo.stats).map(([key, value]) => {
                      if (value === undefined || value === null) return null;
                      return (
                        <div key={key} className="flex items-center space-x-1">
                          {getStatsIcon(key)}
                          <Typography
                            variant="text-sm"
                            className="font-medium text-content-secondary"
                          >
                            {typeof value === "number"
                              ? value.toLocaleString()
                              : String(value)}
                          </Typography>
                          <Typography
                            variant="text-xs"
                            className="capitalize text-content-tertiary"
                          >
                            {key}
                          </Typography>
                        </div>
                      );
                    })}
                  </div>
                )}

                {/* Métadonnées */}
                <div className="flex items-center justify-between text-sm text-content-secondary">
                  <div className="flex items-center space-x-4">
                    <div className="flex items-center space-x-1">
                      <FaClock className="size-4" />
                      <span>
                        {contentInfo.date && formatDate(contentInfo.date)}
                      </span>
                    </div>
                    {contentInfo.language && (
                      <div className="flex items-center space-x-1">
                        <FaGlobe className="size-4" />
                        <span>{contentInfo.language.toUpperCase()}</span>
                      </div>
                    )}
                  </div>
                  {contentInfo.url && (
                    <div className="flex items-center space-x-1 text-accent">
                      <FaShare className="size-4" />
                      <span>{t("view_original")}</span>
                    </div>
                  )}
                </div>
              </div>
            </CardBody>
          </Card>
        );
      })}
    </div>
  );
};

export default CardView;
